import { z } from 'zod';
import { ORDER_STATUSES } from '../domain/order.js';
import { DOMAIN_EVENT_TYPES } from './events.js';
/** Все входные данные gateway проходят через эти схемы — руками ничего не парсим. */
const storeId = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9][a-z0-9_-]*$/, 'идентификатор магазина: a-z, 0-9, _ и -');
/** Ключ заказа в пути запроса: `/api/orders/:ref`. */
export const orderRefSchema = z
    .string()
    .min(3)
    .max(160)
    .regex(/^[a-z0-9_-]+:[A-Za-z0-9_-]+$/, 'ожидается ref вида «store:id»');
const csvList = (max) => z
    .string()
    .max(512)
    .transform((value) => value
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .slice(0, max));
export const authTelegramSchema = z.object({
    /** сырая строка initData из Telegram WebApp, подпись проверяется на сервере */
    initData: z.string().min(1).max(8192),
});
export const refreshSchema = z.object({
    refreshToken: z.string().min(20).max(512),
});
export const orderListQuerySchema = z.object({
    stores: csvList(16).optional(),
    status: csvList(ORDER_STATUSES.length)
        .optional()
        .refine((list) => !list || list.every((s) => ORDER_STATUSES.includes(s)), 'неизвестный статус заказа'),
    /** поиск: номер заказа, телефон, ТТН, имя получателя */
    search: z.string().trim().max(128).optional(),
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(20),
    page: z.coerce.number().int().min(1).max(1000).default(1),
    sort: z
        .enum(['-createdAt', 'createdAt', '-updatedAt', '-amount', 'amount'])
        .default('-createdAt'),
});
export const orderStatusUpdateSchema = z.object({
    status: z.enum(ORDER_STATUSES),
    /**
     * Просьба не трогать клиента (SMS/Telegram). Магазин может так не уметь —
     * тогда gateway ответит 409, а не сделает рассылку молча.
     */
    silent: z.boolean().default(false),
    /** статус, который мы видели перед изменением: защита от гонки двух менеджеров */
    expectedStatus: z.enum(ORDER_STATUSES).optional(),
});
export const orderCreateSchema = z.object({
    storeId,
    items: z
        .array(z.object({
        productId: z.string().min(1).max(64),
        variantId: z.string().min(1).max(64).optional(),
        quantity: z.number().int().min(1).max(999),
        /**
         * Цена за единицу. Payload считает сумму сам по текущим ценам и это
         * поле игнорирует; CRM на CodeIgniter ждёт цену в позиции, поэтому
         * интерфейс подставляет её из результатов поиска товара.
         */
        price: z.number().min(0).max(10_000_000).optional(),
    }))
        .min(1)
        .max(50),
    recipientFirstName: z.string().trim().min(1).max(100),
    recipientLastName: z.string().trim().min(1).max(100),
    recipientPhone: z.string().trim().min(9).max(20),
    deliveryMethod: z.string().min(1).max(64),
    cityName: z.string().trim().min(1).max(200),
    branchName: z.string().trim().min(1).max(300),
    cityRef: z.string().max(64).optional(),
    warehouseRef: z.string().max(64).optional(),
    paymentMethod: z.string().min(1).max(64).default('cod'),
    discountPercent: z.number().min(0).max(100).optional(),
    discountAmount: z.number().min(0).max(1_000_000).optional(),
    comment: z.string().trim().max(1000).optional(),
    skipSms: z.boolean().default(false),
});
export const orderUpdateSchema = z
    .object({
    items: z
        .array(z.object({
        productId: z.string().min(1).max(64),
        variantId: z.string().min(1).max(64).nullable().optional(),
        quantity: z.number().int().min(1).max(999),
        price: z.number().min(0).max(10_000_000),
    }))
        .min(1)
        .max(50)
        .optional(),
    recipientName: z.string().trim().min(1).max(100).optional(),
    recipientPhone: z.string().trim().min(9).max(20).optional(),
    deliveryMethod: z.string().min(1).max(64).optional(),
    deliveryCityName: z.string().trim().min(1).max(200).optional(),
    deliveryCityRef: z.string().max(64).optional(),
    deliveryBranchName: z.string().trim().min(1).max(300).optional(),
    deliveryBranchRef: z.string().max(64).optional(),
    paymentMethod: z.string().min(1).max(64).optional(),
    discountPercent: z.number().min(0).max(100).optional(),
    comment: z.string().trim().max(1000).nullable().optional(),
})
    .refine((patch) => Object.keys(patch).length > 0, 'пустой запрос на изменение заказа');
export const customerListQuerySchema = z.object({
    stores: csvList(16).optional(),
    search: z.string().trim().max(128).optional(),
    nonPickup: z.coerce.boolean().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(20),
    page: z.coerce.number().int().min(1).max(1000).default(1),
});
export const customerPatchSchema = z
    .object({
    nonPickup: z.boolean().optional(),
    noCall: z.boolean().optional(),
    managerNotes: z.string().trim().max(2000).optional(),
    firstName: z.string().trim().max(100).optional(),
    lastName: z.string().trim().max(100).optional(),
    telegram: z.string().trim().max(100).optional(),
})
    .refine((patch) => Object.keys(patch).length > 0, 'пустой запрос на изменение');
export const financeQuerySchema = z.object({
    storeId,
    /** `2026-10`; без него — за весь период */
    month: z
        .string()
        .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'месяц в формате ГГГГ-ММ')
        .optional(),
});
export const financeEntryListQuerySchema = financeQuerySchema.extend({
    type: z.enum(['income', 'expense']).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    page: z.coerce.number().int().min(1).max(1000).default(1),
});
export const financeEntryCreateSchema = z.object({
    storeId,
    type: z.enum(['income', 'expense']),
    categoryId: z.string().min(1).max(64),
    amount: z.number().positive().max(100_000_000),
    entryDate: z.string().datetime(),
    note: z.string().trim().max(1000).optional(),
});
export const trackingUpdateSchema = z.object({
    /** номер накладной: приходит со сканера или вводится руками */
    trackingNumber: z
        .string()
        .trim()
        .min(6)
        .max(40)
        .regex(/^[A-Za-z0-9-]+$/, 'номер накладної: цифри, літери й дефіс'),
});
export const smsSendSchema = z.object({
    message: z.string().trim().min(3).max(500),
});
export const customerRiskQuerySchema = z.object({
    storeId,
    phone: z.string().trim().min(9).max(20),
});
export const stockUpdateSchema = z.object({
    storeId,
    quantity: z.number().int().min(0).max(1_000_000),
    inStock: z.boolean().optional(),
});
export const paymentLinkCreateSchema = z.object({
    /** целое число в основной валюте, не больше остатка по заказу */
    amount: z.number().int().positive().max(10_000_000),
});
export const productSearchQuerySchema = z.object({
    storeId,
    q: z.string().trim().min(2).max(100),
    limit: z.coerce.number().int().min(1).max(50).default(20),
});
export const statsQuerySchema = z.object({
    stores: csvList(16).optional(),
    period: z.enum(['today', 'yesterday', 'week', 'month', 'quarter', 'year']).default('today'),
});
export const notificationPrefsSchema = z.object({
    enabled: z.boolean(),
    newOrders: z.boolean(),
    statusChanges: z.boolean(),
    trackingChanges: z.boolean(),
    payments: z.boolean(),
    storeStatus: z.boolean().default(true),
    quietHours: z
        .object({ from: z.number().int().min(0).max(23), to: z.number().int().min(0).max(23) })
        .nullable()
        .default(null),
    storeIds: z.array(storeId).max(16).default([]),
    silentMessages: z.boolean().default(false),
});
export const eventTicketQuerySchema = z.object({
    /** одноразовый тикет: токен доступа в URL не светим, он утекает в логи */
    ticket: z.string().min(20).max(256),
    /** последний полученный seq — чтобы догнать пропущенное после реконнекта */
    lastSeq: z.coerce.number().int().min(0).optional(),
    types: csvList(DOMAIN_EVENT_TYPES.length).optional(),
});
export const deliveryCitiesQuerySchema = z.object({
    storeId,
    carrier: z.enum(['novaposhta', 'meest']),
    q: z.string().trim().min(2).max(100),
});
export const deliveryBranchesQuerySchema = z.object({
    storeId,
    carrier: z.enum(['novaposhta', 'meest']),
    cityRef: z.string().min(1).max(128),
    q: z.string().trim().max(100).optional(),
});
//# sourceMappingURL=schemas.js.map